import { lazy, Suspense } from 'react'
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import './i18n' // Initialize i18n
import { AppConfigProvider } from './context/AppConfigContext'
import { ThemeProvider } from './context/ThemeContext'
import { AuthProvider } from './context/AuthContext'
import Layout from './components/layout/Layout'
import LoadingSpinner from './components/common/LoadingSpinner'
import ProtectedRoute from './components/auth/ProtectedRoute'

// Lazy load pages for code splitting
const EventsListPage = lazy(() =>
  import('./pages/EventsListPage')
)
const EventDetailPage = lazy(() =>
  import('./pages/EventDetailPage')
)
const QualificationsPage = lazy(() =>
  import('./pages/QualificationsPage')
)
const FinalResultsPage = lazy(() =>
  import('./pages/FinalResultsPage')
)
const LiveRacePage = lazy(() => import('./pages/LiveRacePage'))
const GroupManagementPage = lazy(() =>
  import('./pages/GroupManagementPage')
)
const RaceSchedulePage = lazy(() =>
  import('./pages/RaceSchedulePage')
)
const BracketPage = lazy(() => import('./pages/BracketPage'))
const FinalsPage = lazy(() => import('./pages/FinalsPage'))
const ArchivePage = lazy(() => import('./pages/ArchivePage'))
const PasswordResetConfirmPage = lazy(() =>
  import('./pages/PasswordResetConfirmPage')
)
const EmailVerifyPage = lazy(() =>
  import('./pages/auth/EmailVerifyPage')
)
const LoginPage = lazy(() => import('./pages/auth/LoginPage'))
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const AdminDashboard = lazy(() =>
  import('./pages/AdminDashboard')
)
const VeloLeaderboardPage = lazy(() =>
  import('./pages/VeloLeaderboardPage')
)

function App() {
  return (
    <AppConfigProvider>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <Suspense fallback={<LoadingSpinner size="lg" />}>
            <Routes>
            {/* Auth pages (standalone, outside Layout) */}
            <Route path="/auth/login" element={<LoginPage />} />
            <Route path="/auth/register" element={<RegisterPage />} />
            <Route
              path="/reset-password/:uid/:token"
              element={<PasswordResetConfirmPage />}
            />
            <Route
              path="/auth/verify-email/:key"
              element={<EmailVerifyPage />}
            />

            {/* Main app routes (with Layout) */}
            <Route path="/" element={<Layout />}>
              <Route index element={<Navigate to="/events" />} />
              <Route path="events" element={<EventsListPage />} />
              <Route
                path="events/:id"
                element={<EventDetailPage />}
              >
                <Route index element={<Navigate to="groups" />} />
                <Route
                  path="groups"
                  element={<GroupManagementPage />}
                />
                <Route
                  path="qualifications"
                  element={<QualificationsPage />}
                />
                <Route
                  path="eliminations"
                  element={<BracketPage />}
                />
                <Route path="finals" element={<FinalsPage />} />
                <Route
                  path="final-results"
                  element={<FinalResultsPage />}
                />
                <Route path="live" element={<LiveRacePage />} />
                <Route
                  path="schedule"
                  element={<RaceSchedulePage />}
                />
              </Route>
              <Route path="archive" element={<ArchivePage />} />
              <Route
                path="profile"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="dashboard"
                element={
                  <ProtectedRoute requireStaff>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="velo/:trackId"
                element={
                  <ProtectedRoute>
                    <VeloLeaderboardPage />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/events" />} />
            </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
        <ToastContainer
          position="bottom-right"
          autoClose={false}
          hideProgressBar={true}
          newestOnTop
          closeOnClick={false}
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
          limit={3}
        />
      </AuthProvider>
      </ThemeProvider>
    </AppConfigProvider>
  )
}

export default App
